import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { MembershipPlan } from './membershipService';
import {
  formatMembershipMoney,
  getPlanDetailExtra,
  type BillingInterval,
} from './planDetails';

interface MembershipPlanModalProps {
  plan: MembershipPlan;
  billing: BillingInterval;
  onClose: () => void;
  onGetStarted: (plan: MembershipPlan) => void;
}

export const MembershipPlanModal: React.FC<MembershipPlanModalProps> = ({
  plan,
  billing,
  onClose,
  onGetStarted,
}) => {
  const extra = getPlanDetailExtra(plan.tier);
  const price =
    billing === 'monthly' ? plan.monthly_amount : plan.annual_amount;
  const periodLabel = billing === 'monthly' ? '/mo' : '/yr';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="mp-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mp-modal-title"
    >
      <div className="mp-modal-backdrop" onClick={onClose} aria-hidden />
      <div className={`mp-modal mp-modal--${plan.theme}`}>
        <button type="button" className="mp-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="mp-modal-hero">
          {plan.featured && <span className="mp-modal-badge">Most popular</span>}
          <h2 id="mp-modal-title" className="mp-modal-title">
            {plan.name}
          </h2>
          <p className="mp-modal-sub">{plan.subtitle}</p>
          <div className="mp-modal-price-row">
            <span className="mp-modal-price">
              {formatMembershipMoney(price, plan.currency)}
            </span>
            <span className="mp-modal-period">{periodLabel}</span>
          </div>
          <p className="mp-modal-billed">
            {billing === 'monthly'
              ? 'Billed monthly · cancel anytime'
              : `Billed annually · ~${formatMembershipMoney(plan.annual_amount / 12, plan.currency)}/mo equivalent`}
          </p>
          <p className="mp-modal-ideal">{extra.idealFor}</p>
        </div>

        <div className="mp-modal-body">
          <section>
            <h3>Why this plan</h3>
            <ul className="mp-modal-highlights">
              {extra.highlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section>
            <h3>
              {plan.includes_previous_tier && plan.previous_tier_banner
                ? plan.previous_tier_banner
                : 'What’s included'}
            </h3>
            <ul className="mp-modal-perks">
              {[...plan.perks]
                .sort((a, b) => a.sort_order - b.sort_order)
                .map((perk) => (
                  <li key={perk.perk_id}>{perk.label}</li>
                ))}
            </ul>
          </section>

          {extra.sections.map((section) => (
            <section key={section.title}>
              <h3>{section.title}</h3>
              <p>{section.body}</p>
            </section>
          ))}

          <section>
            <h3>Member savings</h3>
            <p>
              This plan includes{' '}
              <strong>{plan.service_discount_percent}% off</strong> eligible CWY
              service orders once your membership is active.
            </p>
          </section>

          <section>
            <h3>FAQ</h3>
            <div className="mp-modal-faqs">
              {extra.faqs.map((faq) => (
                <div key={faq.q} className="mp-modal-faq">
                  <strong>{faq.q}</strong>
                  <p>{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="mp-modal-footer">
          <button type="button" className="mp-modal-secondary" onClick={onClose}>
            Keep comparing
          </button>
          <button
            type="button"
            className={`mp-cta mp-cta--${plan.theme}`}
            onClick={() => onGetStarted(plan)}
          >
            {plan.cta_label || 'Get started'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
